import { useField } from "formik";
import type React from "react";
import type { FocusEventHandler } from "react";
import { useId } from "react";
import ReactSelect, {
  type ActionMeta,
  type CSSObjectWithLabel,
  type MultiValue,
  type Props,
  type PropsValue,
  type SingleValue,
  type StylesConfig,
} from "react-select";
import AsyncSelect from "react-select/async";

export interface OptionType {
  label: string;
  value: string;
  extras?: unknown;
}

const customStyles: StylesConfig<OptionType, boolean> = {
  option: (
    styles: CSSObjectWithLabel,
    { isSelected }: { isSelected: boolean },
  ) => ({
    ...styles,
    backgroundColor: isSelected ? "#4ABE82" : "white",
    color: isSelected ? "white" : "black",
    transition: "all",
    duration: 300,
    "&:hover": {
      ...styles,
      backgroundColor: isSelected ? "#4ABE82" : "#E7E7E7",
      color: isSelected ? "white" : "black",
    },
  }),
};

interface BaseSelectProps {
  label?: string;
  options: readonly OptionType[];
  customClass?: string;
  name: string;
  required?: boolean;
  valueKey?: string;
  placeholder?: string;
  isSearchable?: boolean;
  value?: PropsValue<OptionType>;
  isClearable?: boolean;
  className?: string;
  inputClassName?: string;
  helperText?: string;
  isDisabled?: boolean;
  error?: string;
  helperTextClassName?: string;
  labelClassName?: string;
  intent?: "outline" | "underline";
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onFocus?: FocusEventHandler<HTMLInputElement>;
  openMenuOnFocus?: boolean;
  onMenuOpen?: () => void;
  isLoading?: boolean;
  noOptionsMessage?: Props["noOptionsMessage"];
  isAsync?: boolean;
  loadOptions?: (
    inputValue: string,
    callback?: (options: OptionType[]) => void,
  ) => Promise<OptionType[]> | void;
  cacheOptions?: boolean;
  defaultOptions?: boolean | OptionType[];
  maxMenuHeight?: number;
}

// Discriminated union: isMulti drives the onChange signature
type SingleSelectProps = BaseSelectProps & {
  isMulti?: false;
  onChange?: (
    newValue: SingleValue<OptionType>,
    actionMeta?: ActionMeta<OptionType>,
  ) => void;
};

type MultiSelectProps = BaseSelectProps & {
  isMulti: true;
  onChange?: (
    newValue: MultiValue<OptionType>,
    actionMeta?: ActionMeta<OptionType>,
  ) => void;
};

export type CustomSelectProps = SingleSelectProps | MultiSelectProps;

export const CustomSelect: React.FC<CustomSelectProps> = ({
  label,
  options,
  customClass = "",
  name,
  required,
  helperText,
  error,
  helperTextClassName = "",
  labelClassName = "",
  intent = "outline",
  ...rest
}) => {
  const inputId = useId();

  const outlinedClassName = "!border !border-gray-200 !rounded-lg";
  const underlineClassName =
    "!border-x-0 !border-t-0 !border-b !border-gray-200 !rounded-none";
  const intentClassName =
    intent === "outline" ? outlinedClassName : underlineClassName;

  const onChange = rest.onChange as
    | ((
        newValue: SingleValue<OptionType> | MultiValue<OptionType>,
        actionMeta: ActionMeta<OptionType>,
      ) => void)
    | undefined;

  return (
    <div>
      <div className="flex flex-col items-stretch gap-2">
        {label && (
          <label
            className={`label-text text-sm text-gray-extras-100 ${labelClassName}`}
            htmlFor={inputId}
          >
            {label}
            {required && <span className="text-sm text-red-400"> *</span>}
          </label>
        )}
        {!rest.isAsync ? (
          <ReactSelect<OptionType, boolean>
            className={customClass}
            classNames={{
              control: (props) =>
                `${props.className} h-[48px] !border-gray-300 ${intentClassName}`,
              dropdownIndicator: (props) => `${props.className} !text-green`,
            }}
            components={{ IndicatorSeparator: () => null }}
            id={inputId}
            isMulti={rest.isMulti}
            options={options}
            styles={customStyles}
            {...rest}
            onChange={onChange}
          />
        ) : (
          <AsyncSelect<OptionType, boolean>
            cacheOptions={rest.cacheOptions}
            className={customClass}
            classNames={{
              control: (props) =>
                `${props.className} h-[48px] !border-gray-300 ${intentClassName}`,
              dropdownIndicator: (props) => `${props.className} !text-green`,
            }}
            components={{ IndicatorSeparator: () => null }}
            defaultOptions={rest.defaultOptions}
            id={inputId}
            isMulti={rest.isMulti}
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
            loadOptions={rest.loadOptions as any}
            styles={customStyles}
            {...rest}
            onChange={onChange}
          />
        )}
        {error || helperText ? (
          <span
            className={`text-xs ${
              error ? "text-orange" : "text-gray-400"
            } ${helperTextClassName}`}
          >
            {error ?? helperText}
          </span>
        ) : null}
      </div>
    </div>
  );
};

export const FormSelect: React.FC<CustomSelectProps> = ({
  label,
  options,
  customClass,
  onChange,
  name,
  required,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  valueKey = "value",
  isMulti = false,
  ...rest
}) => {
  const [field, meta, { setValue, setTouched }] = useField(name);

  const isError = meta.touched && meta.error;

  const containerClassName = `mt-1 rounded-md focus:outline-none focus:ring ${
    customClass ?? ""
  } ${isError ? "border-red-500" : ""}`;

  const selectedOption = isMulti
    ? options.filter(
        (option) =>
          field.value &&
          (field.value as OptionType[]).find(
            (x: OptionType | string) =>
              (x as OptionType).value === option.value || x === option.value,
          ),
      )
    : (options.find((option) => option.value === field.value) ?? null);

  const handleChange = (
    value: SingleValue<OptionType> | MultiValue<OptionType>,
  ) => {
    if (isMulti) {
      void setValue(value ?? []);
      (onChange as MultiSelectProps["onChange"])?.(
        value as MultiValue<OptionType>,
      );
    } else {
      const single = value as SingleValue<OptionType>;
      void setValue(single ? single.value : "");
      (onChange as SingleSelectProps["onChange"])?.(single);
    }
  };

  const handleBlur = () => void setTouched(true);

  return (
    <div className="mb-4">
      {label && (
        <p className="mb-1 text-sm text-[#707070]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </p>
      )}
      <ReactSelect<OptionType, boolean>
        {...field}
        className={containerClassName}
        components={{ IndicatorSeparator: () => null }}
        id={label}
        isMulti={isMulti}
        onBlur={handleBlur}
        onChange={handleChange}
        options={options}
        styles={customStyles}
        value={selectedOption}
        {...rest}
      />
      {isError && <div className="text-red-500 mt-1 text-sm">{meta.error}</div>}
    </div>
  );
};
